import type {
  RecentBankTransactionsResponseDTO,
  RecentBankTransactionItemDTO,
} from "../models/recentBankTransactionsDto";
import type {
  RecentBankTransactionsEntity,
  RecentBankTransactionItemEntity,
} from "../../domain/entities/RecentBankTransactionsEntity";

function formatDateShort(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatCategoryLabel(raw: string | null | undefined): string {
  const value = (raw ?? "Uncategorized").trim();
  if (!value) {
    return "Uncategorized";
  }
  // Convert snake_case or lower/upper to \"Title Case\" with spaces
  const cleaned = value.replace(/_/g, " ");
  return cleaned
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function itemDtoToEntity(
  dto: RecentBankTransactionItemDTO
): RecentBankTransactionItemEntity {
  const isCredit = (dto.debit_or_credit ?? "").toLowerCase() === "credit";
  const reviewStatusRaw = (dto.review_status ?? "").toLowerCase();
  const reviewStatus =
    reviewStatusRaw === "pending"
      ? "pending"
      : reviewStatusRaw === "approved"
        ? "approved"
        : reviewStatusRaw === "rejected"
          ? "rejected"
          : null;
  const categoryLabel = formatCategoryLabel(dto.category);
  const isUncategorized = categoryLabel === "Uncategorized";
  const categoryVariant: "neutral" | "warning" | "success" =
    reviewStatus === "pending" || isUncategorized ? "warning" : "neutral";

  return {
    id: dto.id,
    date: formatDateShort(dto.date),
    accountName: dto.accountName ?? "—",
    description: dto.description ?? "—",
    category: categoryLabel,
    categoryVariant,
    amount: dto.amount ?? 0,
    isCredit,
    status:
      dto.status ??
      (reviewStatus === "pending" ? "Needs review" : "Cleared"),
    reviewStatus,
  };
}

export function recentBankTransactionsDtoToEntity(
  dto: RecentBankTransactionsResponseDTO
): RecentBankTransactionsEntity {
  return {
    items: (dto.items ?? []).map(itemDtoToEntity),
    pagination: {
      total: dto.pagination?.total ?? 0,
      limit: dto.pagination?.limit ?? 10,
      page: dto.pagination?.page ?? 1,
      totalPages: dto.pagination?.total_pages ?? 0,
      hasMore: dto.pagination?.has_more ?? false,
    },
  };
}
